import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calculator, Mail } from "lucide-react";
import { guides } from "@/data/guides";
import { usePageMeta } from "@/hooks/usePageMeta";

const EASE = [0.16, 1, 0.3, 1];

export default function GuidePage() {
  const { slug } = useParams();
  const guide = guides.find((g) => g.slug === slug);

  usePageMeta(
    guide ? `${guide.title} | ConstruireMaison.net` : "Guides | ConstruireMaison.net",
    guide ? guide.excerpt : "Guides gratuits pour construire sa maison : budget, terrain, étapes, PTZ."
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    if (guide) {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const post = (event) =>
        fetch(API + "/track", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ event, slug: guide.slug }),
        }).catch(() => {});
      post("guide_view");
      if (guide.videoId) post("video_view");
    }
  }, [slug]);

  if (!guide) return <Navigate to="/" replace />;

  return (
    <div data-testid="guide-page">
      <header className="border-b border-border bg-bone/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-sub font-extrabold tracking-tight text-xl" data-testid="guide-logo">
            construire<span className="text-terra">maison</span>.net
          </Link>
          <Link
            to="/"
            data-testid="guide-back-link"
            className="inline-flex items-center gap-2 font-sub text-sm text-ink/70 hover:text-terra transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-6 py-16 lg:py-24">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
          <p className="font-sub text-xs uppercase tracking-[0.2em] font-bold text-terra mb-4">{guide.category}</p>
          <h1 className="font-display text-4xl sm:text-5xl leading-[1.1] tracking-tight mb-6" data-testid="guide-title">
            {guide.title}
          </h1>
          <p className="text-lg leading-relaxed text-ink/70 mb-10">{guide.excerpt}</p>
          {guide.videoId ? (
            <div className="rounded-xl overflow-hidden border border-border shadow-lift mb-16 bg-ink">
              <div className="aspect-video">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${guide.videoId}?rel=0`}
                  title={guide.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  data-testid="guide-video-iframe"
                />
              </div>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden border border-border shadow-lift mb-16">
              <img src={guide.img} alt={guide.title} className="w-full aspect-[2/1] object-cover" />
            </div>
          )}
        </motion.div>

        {guide.sections.map((s, i) => (
          <motion.section
            key={s.heading}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-12"
            data-testid={`guide-section-${i + 1}`}
          >
            <h2 className="font-display text-2xl sm:text-3xl leading-snug mb-4">{s.heading}</h2>
            {s.paragraphs.map((p, j) => (
              <p key={j} className="text-base leading-relaxed text-ink/75 mb-4">{p}</p>
            ))}
          </motion.section>
        ))}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="bg-sand rounded-xl border border-border p-8 lg:p-12 mt-16"
          data-testid="guide-cta"
        >
          <h3 className="font-display text-2xl sm:text-3xl leading-snug mb-4">
            Passez à l'action
          </h3>
          <p className="text-base text-ink/70 leading-relaxed mb-8 max-w-lg">
            Chiffrez votre projet avec nos simulateurs gratuits, ou recevez chaque nouveau guide par email.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="/#simulateurs"
              data-testid="guide-cta-simulators"
              className="inline-flex items-center justify-center gap-2 bg-terra text-white font-sub text-sm font-medium px-6 py-3.5 rounded-full hover:bg-terra-dark hover:-translate-y-0.5 transition-[background-color,transform] duration-200"
            >
              <Calculator className="w-4 h-4" />
              Ouvrir les simulateurs
            </a>
            <a
              href="/#newsletter"
              data-testid="guide-cta-newsletter"
              className="inline-flex items-center justify-center gap-2 font-sub text-sm font-medium px-6 py-3.5 rounded-full border border-ink/20 hover:border-ink hover:-translate-y-0.5 transition-[border-color,transform] duration-200"
            >
              <Mail className="w-4 h-4" />
              Recevoir les guides par email
            </a>
          </div>
        </motion.div>
      </article>
    </div>
  );
}
